import { useState } from "react"
import { CampaignFeatureConfig } from "../../interfaces/campaignConfig"
import { Alert, Box, Button, Paper, Snackbar, Step, StepLabel, Stepper, Typography } from "@mui/material"
import StepOneConfig from "./stepOneConfig"
import StepTwoUploadValidate from "./stepTwoUploadValidate"
import StepThreeSend from "./stepThreeSend"

export default function CampaignFeatureFlow({ feature }: { feature: CampaignFeatureConfig }) {
    // Use feature-specific steps if provided, otherwise fallback to default 3-step flow
    const steps = feature.steps;
    const [activeStep, setActiveStep] = useState(0)
    const [inputsState, setInputsState] = useState<Record<string, any>>({})
    const [file, setFile] = useState<File | null>(null)
    const [isValid, setIsValid] = useState<boolean | null>(null)
    const [validationMessage, setValidationMessage] = useState<string | null>(null)
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' })

    const handleNext = () => {
        // basic guards
        if (activeStep === 0) {
            // ensure required inputs exist (simple check: non-empty)
            const missing = (feature.inputs ?? []).filter((i) => !inputsState[i.name])
            if (missing.length > 0) return setSnackbar({ open: true, message: `Please fill ${missing.map(m => m.name).join(', ')}`, severity: 'error' })
        }
        if (activeStep === 1) {
            // must have uploaded file
            if (!file) return setSnackbar({ open: true, message: 'Please upload a file first', severity: 'error' })
            // if file exists but hasn't been validated (isValid !== true), block and ask to validate
            if (isValid !== true) return setSnackbar({ open: true, message: 'Please validate the file first', severity: 'error' })
        }
        setActiveStep((s) => Math.min(s + 1, steps.length - 1))
    }
    const handleBack = () => setActiveStep((s) => Math.max(0, s - 1))
    const handleReset = () => {
        setActiveStep(0)
        setInputsState({})
        setFile(null)
        setIsValid(null)
        setValidationMessage(null)
    }


    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>{feature.label}</Typography>


            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
                {steps.map((label) => (
                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
            </Stepper>


            <Box mt={1}>
                {activeStep === 0 && (
                    <StepOneConfig feature={feature} inputsState={inputsState} setInputsState={setInputsState} />
                )}


                {activeStep === 1 && (
                    <StepTwoUploadValidate
                        feature={feature}
                        inputsState={inputsState}
                        file={file}
                        setFile={setFile}
                        isValid={isValid}
                        setIsValid={setIsValid}
                        setValidationMessage={setValidationMessage}
                        onValidated={() => setActiveStep(2)}
                    />
                )}

                {activeStep === 2 && (
                    <StepThreeSend feature={feature} inputsState={inputsState} file={file} isValid={isValid} setSnackbar={(s) => setSnackbar(s)} onSuccess={handleReset} />
                )}

                <Box display="flex" gap={2} mt={2}>
                    <Button variant="contained" onClick={handleBack} disabled={activeStep === 0}>Back</Button>
                    <Button variant="contained" onClick={handleNext} disabled={activeStep === steps.length - 1}>Next</Button>
                    <Button variant="text" onClick={handleReset}>Reset</Button>
                </Box>

                {validationMessage && <Box mt={2}><Alert severity={isValid ? 'success' : 'error'}>{validationMessage}</Alert></Box>}


                <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
                </Snackbar>
            </Box>
        </Paper>
    )
}